"use client";

import { useEffect, useState, createContext, useContext, useCallback, type ReactNode } from "react";

/**
 * Footer connection status context.
 * Placed in the root layout so both ChatLayout (setter) and Footer (reader)
 * can access it regardless of component tree position.
 */
type WsStatus = "connecting" | "authenticated" | "disconnected";

interface FooterStatusContextValue {
  status: WsStatus | null;
  setStatus: (s: WsStatus) => void;
}

const FooterStatusContext = createContext<FooterStatusContextValue>({
  status: null,
  setStatus: () => {},
});

export function useSetFooterStatus() {
  return useContext(FooterStatusContext).setStatus;
}

export function useFooterStatus() {
  return useContext(FooterStatusContext).status;
}

export function FooterStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<WsStatus | null>(null);
  const set = useCallback((s: WsStatus) => setStatus(s), []);

  return (
    <FooterStatusContext.Provider value={{ status, setStatus: set }}>
      {children}
    </FooterStatusContext.Provider>
  );
}

export function Footer() {
  const [version, setVersion] = useState<string | null>(null);
  const wsStatus = useFooterStatus();

  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? "/api";
    fetch(`${apiUrl}/status`)
      .then((r) => r.json())
      .then((d) => setVersion(d.version))
      .catch(() => setVersion("unavailable"));
  }, []);

  const statusLabel =
    wsStatus === "authenticated" ? "connected" :
    wsStatus === "connecting" ? "connecting..." :
    wsStatus === "disconnected" ? "disconnected" :
    null;

  const statusColor =
    wsStatus === "authenticated" ? "#999" :
    wsStatus === "connecting" ? "#8a7a30" :
    wsStatus === "disconnected" ? "#7a3030" :
    undefined;

  return (
    <footer
      suppressHydrationWarning
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        textAlign: "center",
        padding: "0.4rem",
        borderTop: "1px solid var(--color-border)",
        fontSize: "0.7rem",
        color: "var(--color-text-faint)",
        background: "var(--color-bg)",
        zIndex: 10,
      }}
    >
      {version ? `v${version}` : "\u00A0"}{" \u00B7 "}
      <a
        href="https://api.occum.net/v1/openapi"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "var(--color-text-faintest)", textDecoration: "none" }}
      >
        API docs
      </a>
      {statusLabel && (
        <>
          {" \u00B7 "}
          <span style={{ color: statusColor }}>{statusLabel}</span>
        </>
      )}
    </footer>
  );
}
